import { useSyncExternalStore } from 'react';
import { BREAKPOINTS } from '@/consts/breakpoints';

interface DeviceViewport {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const tabletQuery = `(min-width: ${BREAKPOINTS.desktop}px)`;
const desktopQuery = `(min-width: ${BREAKPOINTS.largeDesktop}px)`;

let tabletMq: MediaQueryList | null = null;
let desktopMq: MediaQueryList | null = null;

const getTabletMq = () => {
  if (!tabletMq) tabletMq = window.matchMedia(tabletQuery);
  return tabletMq;
};

const getDesktopMq = () => {
  if (!desktopMq) desktopMq = window.matchMedia(desktopQuery);
  return desktopMq;
};

const buildSnapshot = (): DeviceViewport => {
  const isDesktop = getDesktopMq().matches;
  const isTabletOrAbove = getTabletMq().matches;
  return {
    isMobile: !isTabletOrAbove,
    isTablet: isTabletOrAbove && !isDesktop,
    isDesktop,
  };
};

let cached: DeviceViewport = buildSnapshot();

const subscribe = (cb: () => void) => {
  const tMq = getTabletMq();
  const dMq = getDesktopMq();

  const onChange = () => {
    const next = buildSnapshot();
    if (
      next.isMobile !== cached.isMobile ||
      next.isTablet !== cached.isTablet ||
      next.isDesktop !== cached.isDesktop
    ) {
      cached = next;
      cb();
    }
  };

  tMq.addEventListener('change', onChange);
  dMq.addEventListener('change', onChange);
  return () => {
    tMq.removeEventListener('change', onChange);
    dMq.removeEventListener('change', onChange);
  };
};

const getSnapshot = () => cached;

/**
 * Reactive viewport hook — returns device type based on viewport width.
 * Uses matchMedia for native threshold detection without resize polling.
 *
 * - isMobile: < 768px
 * - isTablet: >= 768px and < 1024px
 * - isDesktop: >= 1024px
 */
export const useDeviceViewport = () =>
  useSyncExternalStore(subscribe, getSnapshot);
